import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { invariant } from '@epic-web/invariant'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { toast } from 'sonner'
import { usePathname, useRouter } from 'next/navigation'
import { useUserStore } from '~/app/_store/user-store'
import { getId } from '@basehub/utils/id/index'
import { isHotkey } from 'is-hotkey'
import {
  RadioGroup,
  RadioGroupProps,
  RadioItemWithLabel,
} from '~/sotano-kit/primitives/radio-group'
import { Input, LabeledInput } from '~/sotano-kit/primitives/input'
import { clsx } from 'clsx'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { Repository, Team } from '@basehub/data/replicache/index'
import { Button } from '~/sotano-kit/primitives/button'
import { EditorSettingPageTitle } from '../../_components/setting-page-title'

export const RepositorySettings = ({
  repository,
  team,
}: {
  repository: Repository | PublicRepo
  team: Team | PublicTeam
}) => {
  const teamReplicache = useTeamReplicache().data?.replicache
  const router = useRouter()
  const user = useUserStore((s) => s.optimisticUser)
  const pathname = usePathname()
  const focusRef = React.useRef<'title' | 'description'>(undefined)

  const {
    mutate: handleSave,
    error,
    isLoading,
    isError,
  } = useMutation(
    ['repoPublicInfoUpdate', repository?.id],
    async (formData: FormData) => {
      invariant(teamReplicache, 'Replicache must be defined')
      if (!repository || !team) return null

      const title = formData.get('Title')?.toString()
      const description = formData.get('Description')?.toString()
      const previewUrl = formData.get('Website URL')?.toString()
      const gitRepoUrl = formData.get('Git Repository URL')?.toString()
      const visibilityValue = formData.get('Visibility')?.toString()
      invariant(visibilityValue, 'Visibility value must be defined')
      const isPublic = visibilityValue === 'public'

      const currentSlug = repository.slug
      let newSlug: string | undefined

      if (title && title !== repository.title) {
        invariant(user?.id, 'User must be defined')

        newSlug = (
          await teamReplicache.mutate.renameRepository({
            repoId: repository.id,
            newTitle: title,
            isoNow: new Date().toISOString(),
            teamId: team.id,
            eventId: getId(),
            userId: user.id,
          })
        ).newSlug
      }

      await teamReplicache.mutate.updateRepositoryPublicInfo({
        repoId: repository.id,
        description,
        previewUrl,
        gitRepoUrl,
        isPublic,
      })

      if (newSlug) {
        router.push(pathname.replace(currentSlug, newSlug))
      }
    },
    {
      onSuccess: () => {
        toast.success('Repository updated successfully.')
      },
    }
  )

  return (
    <form action={handleSave} className="max-w-[600px]">
      <EditorSettingPageTitle>General</EditorSettingPageTitle>
      <TextOption
        name="Title"
        className="-mt-2.5"
        initialValue={repository.title}
        inputProps={{
          required: true,
          autoFocus: !focusRef.current || focusRef.current === 'title',
          onFocus: (e) => {
            if (focusRef.current === 'title') {
              e.currentTarget.select()
            }
          },
        }}
      />
      <TextOption
        name="Description"
        placeholder="Something about your repository"
        multiLine
        inputProps={{
          autoFocus: focusRef.current === 'description',
          onFocus: (e) => {
            if (focusRef.current === 'description') {
              e.currentTarget.select()
            }
          },
          onKeyDown: (e) => {
            if (isHotkey('mod+enter', e)) {
              e.preventDefault()
              const formData = new FormData(
                e.currentTarget.form as HTMLFormElement
              )
              handleSave(formData)
            }
          },
        }}
        initialValue={repository.description ?? ''}
      />
      <RelevantLinkOption
        placeholder="Link to your website"
        url={repository.previewUrl ?? undefined}
        label="Website URL"
      />
      <RelevantLinkOption
        placeholder="Link to your git repository"
        url={repository.gitRepoUrl ?? undefined}
        label="Git Repository URL"
      />
      <div className="text-xs text-dark-muted flex grow flex-col gap-y-1.5 pt-2.5">
        <span className="font-semibold">Visibility</span>
        <IsPublicRadioGroup isPublic={Boolean(repository.isPublic)} />
      </div>

      {/* <div className="flex items-center justify-end gap-2 mt-9 relative"> */}
      {isError && (
        <span className="flex w-full ml-px text-xs leading-4 gap-x-1 text-dark-error absolute -translate-y-8">
          <span className="truncate">{(error as Error).message}</span>
        </span>
      )}

      <div className="my-6 flex items-center gap-2">
        <Button type="button" intent="secondary">
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          Save
        </Button>
      </div>
    </form>
  )
}

const TextOption = ({
  name,
  initialValue,
  placeholder,
  type = 'text',
  inputProps,
  multiLine = false,
  className,
}: {
  name: string
  initialValue: string
  type?: string
  placeholder?: string
  inputProps?: React.ComponentProps<typeof Input>
  multiLine?: boolean
  className?: string
}) => {
  const id = React.useId()
  const [value, setValue] = React.useState(initialValue)

  return (
    <LabeledInput
      className={clsx('!pt-2.5 !pb-2', className)}
      htmlFor={id}
      label={name}
      size="xs"
      name={name}
      blocked={false}
      inputProps={{
        value,
        onChange: (e) => setValue(e.target.value),
        asChild: multiLine,
        id: id,
        autoComplete: 'off',
        type,
        size: 'xs',
        placeholder: typeof placeholder === 'string' ? placeholder : undefined,
        className: clsx(multiLine && 'py-2 min-h-18'),
        children: multiLine ? (
          <textarea className="min-h-20 scroll-py-2 max-h-44 px-3 resize-none" />
        ) : null,
        ...inputProps,
      }}
    />
  )
}

const RelevantLinkOption = ({
  url,
  label,
  inputProps,
  placeholder,
}: {
  url?: string
  label: string
  inputProps?: React.ComponentProps<typeof Input>
  placeholder?: string
}) => {
  return (
    <LabeledInput
      className="!py-2.5 !text-xs"
      blocked={false}
      label={label}
      name={label}
      inputProps={{
        maxLength: 64,
        placeholder,
        autoComplete: 'off',
        size: 'xs',
        defaultValue: url,
        ...inputProps,
      }}
    />
  )
}

export const IsPublicRadioGroup = ({
  isPublic,
  ...props
}: {
  isPublic: boolean
} & RadioGroupProps) => {
  return (
    <RadioGroup
      // {...baseProps}
      name="Visibility"
      className="flex items-center group/root gap-6 text-xs text-dark-muted"
      defaultValue={isPublic ? 'public' : 'private'}
      // onValueChange={handleChange}
      aria-label="Public or Private"
      {...props}
    >
      <RadioItemWithLabel isActive={isPublic} value="public">
        Public
      </RadioItemWithLabel>
      <RadioItemWithLabel isActive={!isPublic} value="private">
        Private
      </RadioItemWithLabel>
    </RadioGroup>
  )
}
